// client/src/coms/voip/voiceclient_parts/proximity.js
import { getParticipantPrefs } from '../participantPrefs';
import { getVoiceMasterVolume } from '../voiceMasterVolume';
import {
  audioElsByTrackSid,
  audioMetaByTrackSid,
  PROXIMITY_POLL_MS,
  PROXIMITY_IDLE_POLL_MS,
  PROXIMITY_VOLUME_STOPS,
  voiceState,
} from './state';
import { clamp01, extractNumericIdentityId, fetchLocalVoiceAnimationFromBackend, getActiveLocalUserId, parseComsRoomName } from './utils';
import { broadcastLocalVoiceAnimation } from './packets';

export function setActiveProximityState(next) {
  voiceState.activeProximityState = next && typeof next === 'object' ? next : null;
  try {
    applyRemoteAudioElementPrefs(voiceState.activeRoom, {});
  } catch {}
}

async function fetchProximityStateForRoom(roomName) {
  const cleanRoom = String(roomName || '').trim();
  if (!cleanRoom) return { enabled: false, proximity: null };

  const res = await fetch('/api/coms/voice/proximity/state', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      room: cleanRoom,
    }),
  });

  if (!res.ok) return { enabled: false, proximity: null };

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  const enabled = !!(data && data.enabled);
  const proximity = enabled && data && data.proximity && typeof data.proximity === 'object'
    ? data.proximity
    : null;

  return { enabled, proximity };
}

export async function refreshProximityNow() {
  const roomName = String(voiceState.activeComsRoomName || '').trim();
  if (!roomName) return;

  if (voiceState.proximityPollInFlight) return;
  voiceState.proximityPollInFlight = true;

  try {
    const next = await fetchProximityStateForRoom(roomName);
    voiceState.proximityEnabled = !!(next && next.enabled);
    setActiveProximityState(next && next.enabled ? next.proximity : null);
  } catch {
  } finally {
    voiceState.proximityPollInFlight = false;
  }
}

let proximityPollGeneration = 0;

export function stopProximityPolling() {
  proximityPollGeneration += 1;
  if (voiceState.proximityPollTimer) {
    clearTimeout(voiceState.proximityPollTimer);
    voiceState.proximityPollTimer = null;
  }
}

export function startProximityPolling(roomName) {
  const cleanRoom = String(roomName || '').trim();
  stopProximityPolling();

  voiceState.activeComsRoomName = cleanRoom;

  const parsed = parseComsRoomName(cleanRoom);
  voiceState.activeServerId = parsed && parsed.serverId ? String(parsed.serverId) : '';

  if (!cleanRoom) {
    voiceState.proximityEnabled = false;
    setActiveProximityState(null);
    return;
  }

  // Fast polling only while proximity is active (map tokens move); every voice user polled 4x/s before.
  const generation = proximityPollGeneration;
  const tick = async () => {
    try {
      await refreshProximityNow();
    } catch {}
    if (generation !== proximityPollGeneration || voiceState.activeComsRoomName !== cleanRoom) return;
    voiceState.proximityPollTimer = setTimeout(tick, voiceState.proximityEnabled ? PROXIMITY_POLL_MS : PROXIMITY_IDLE_POLL_MS);
  };
  tick();
}

function findTokenByUserId(tokens, userId) {
  const uid = Number(userId || 0);
  if (!uid || !Array.isArray(tokens)) return null;

  for (const t of tokens) {
    if (!t || Number(t.userId || 0) !== uid) continue;
    const x = Number(t.x);
    const y = Number(t.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    return { ...t, x, y };
  }

  return null;
}

function computeDistanceMetresBetweenTokens(a, b, metreInPixels) {
  const mpp = Number(metreInPixels || 0);
  if (!a || !b || !Number.isFinite(mpp) || mpp <= 0) return Infinity;
  const dx = Number(a.x) - Number(b.x);
  const dy = Number(a.y) - Number(b.y);
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return Infinity;
  return Math.hypot(dx, dy) / mpp;
}

function computeDistanceAttenuation(distanceMetres) {
  const d = Number(distanceMetres);
  if (!Number.isFinite(d)) return 1;

  const stops = Array.isArray(PROXIMITY_VOLUME_STOPS) ? PROXIMITY_VOLUME_STOPS : [];
  if (!stops.length) return 1;

  const normalizedStops = stops
    .map((stop) => ({
      distance: Number(stop && stop.distance),
      volume: clamp01(stop && stop.volume),
    }))
    .filter((stop) => Number.isFinite(stop.distance))
    .sort((a, b) => a.distance - b.distance);

  if (!normalizedStops.length) return 1;

  if (d <= normalizedStops[0].distance) {
    return normalizedStops[0].volume;
  }

  for (let i = 1; i < normalizedStops.length; i++) {
    const prev = normalizedStops[i - 1];
    const next = normalizedStops[i];

    if (d <= next.distance) {
      const span = next.distance - prev.distance;
      if (span <= 0) return next.volume;
      const t = (d - prev.distance) / span;
      const v = prev.volume + (next.volume - prev.volume) * t;
      return clamp01(v);
    }
  }

  return normalizedStops[normalizedStops.length - 1].volume;
}

function computeRemoteProximityVolume(meta) {
  try {
    if (voiceState.manualDeafened) return 0;
    if (voiceState.proximityEnabled === false) return 1;

    const state = voiceState.activeProximityState;
    if (!state || typeof state !== 'object') return 1;

    const myUserId = getActiveLocalUserId();
    const remoteUserId = extractNumericIdentityId(meta && meta.participantIdentity);

    if (!myUserId || !remoteUserId) return 1;
    if (myUserId === remoteUserId) return 1;

    const metreInPixels = Number(state.metreInPixels || 0);
    const tokens = Array.isArray(state.tokens) ? state.tokens : [];

    if (!Number.isFinite(metreInPixels) || metreInPixels <= 0) return 1;

    const myToken = findTokenByUserId(tokens, myUserId);
    const remoteToken = findTokenByUserId(tokens, remoteUserId);

    if (!myToken || !remoteToken) return 1;

    const distMetres = computeDistanceMetresBetweenTokens(myToken, remoteToken, metreInPixels);
    return computeDistanceAttenuation(distMetres);
  } catch {
    return 1;
  }
}

export function computeRemoteElementVolume(meta, opts = {}) {
  if (voiceState.manualDeafened) return 0;

  const serverId = String(opts.serverId || voiceState.activeServerId || '').trim();
  const participantIdNum = extractNumericIdentityId(meta && meta.participantIdentity);
  const participantId = participantIdNum ? String(participantIdNum) : '';
  const participantName = String((meta && meta.participantName) || '').trim() || String((meta && meta.participantIdentity) || '').trim();

  let pref = null;

  if (serverId && participantId) {
    try {
      pref = getParticipantPrefs({
        serverId,
        participantId,
        participantName,
      });
    } catch {
      pref = null;
    }
  }

  const muted = !!(pref && pref.muted);
  if (muted) return 0;

  const raw = Number(pref && pref.volume != null ? pref.volume : 100);
  const vol = raw > 1 ? raw / 100 : raw;
  const proximity = computeRemoteProximityVolume(meta);
  return clamp01(clamp01(vol) * clamp01(proximity) * getVoiceMasterVolume());
}

export function applyRemoteAudioElementPrefs(room, opts = {}) {
  const r = room || voiceState.activeRoom || null;
  if (!r) return;

  const serverId = String(opts.serverId || voiceState.activeServerId || '').trim();
  const participantIdFilter = String(opts.participantId || '').trim();
  const participantIdFilterNum = extractNumericIdentityId(participantIdFilter);

  for (const [sid, el] of audioElsByTrackSid.entries()) {
    if (!el) continue;

    const meta = audioMetaByTrackSid.get(sid) || {};
    const participantIdNum = extractNumericIdentityId(meta.participantIdentity);

    if (participantIdFilterNum && participantIdNum !== participantIdFilterNum) continue;

    const nextVol = computeRemoteElementVolume(meta, { serverId });

    try {
      el.volume = nextVol;
    } catch {}

    try {
      el.muted = false;
    } catch {}
  }
}

export function applyRemoteAudioAudibility() {
  if (voiceState.meterMonitorOn) return;
  try {
    applyRemoteAudioElementPrefs(voiceState.activeRoom, {});
  } catch {}
}

export function bindParticipantPrefsOnce() {
  if (voiceState.participantPrefsBound) return;
  voiceState.participantPrefsBound = true;

  window.addEventListener(
    'pa-coms-voice-participant-prefs-changed',
    (e) => {
      try {
        const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : {};
        const serverId = detail.serverId != null ? String(detail.serverId) : '';
        const participantId = detail.participantId != null ? String(detail.participantId) : '';
        applyRemoteAudioElementPrefs(voiceState.activeRoom, { serverId, participantId });
      } catch {}
    },
    { passive: true }
  );
}

export function bindProximityOnce() {
  if (voiceState.proximityBound) return;
  voiceState.proximityBound = true;

  window.addEventListener(
    'focus',
    () => {
      refreshProximityNow().catch(() => {});
    },
    { passive: true }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      try {
        if (document.visibilityState === 'visible') {
          refreshProximityNow().catch(() => {});
        }
      } catch {}
    },
    { passive: true }
  );
}

export function bindVoiceAnimationOnce() {
  if (voiceState.voiceAnimationBound) return;
  voiceState.voiceAnimationBound = true;

  window.addEventListener(
    'vox-voice-animation-changed',
    async () => {
      try {
        await fetchLocalVoiceAnimationFromBackend();
        broadcastLocalVoiceAnimation(true);
      } catch {}
    },
    { passive: true }
  );
}
